import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { User, Briefcase, ArrowLeft, Users, Gift, FileText, CheckCircle2, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'yacht', label_en: 'Yachts', label_es: 'Yates' },
  { id: 'tour', label_en: 'Tours', label_es: 'Tours' },
  { id: 'activity', label_en: 'Activities', label_es: 'Actividades' },
  { id: 'party', label_en: 'Parties', label_es: 'Fiestas' },
  { id: 'car_rental', label_en: 'Car Rental', label_es: 'Renta de Autos' },
  { id: 'lodging', label_en: 'Lodging', label_es: 'Hospedaje' }
];

const LOCATIONS = [
  { id: 'playa_del_carmen', label: 'Playa del Carmen' },
  { id: 'cancun', label: 'Cancún' },
  { id: 'tulum', label: 'Tulum' },
  { id: 'quintana_roo', label: 'Quintana Roo' },
  { id: 'los_cabos', label: 'Los Cabos' }
];

function DocUploadField({ fieldId, labelEs, labelEn, value, uploading, onSelect, onRemove, language }) {
  const hasFile = !!value;
  return (
    <div>
      <Label className="text-xs">{language === 'en' ? labelEn : labelEs}</Label>
      {hasFile ? (
        <div
          data-testid={`doc-${fieldId}-uploaded`}
          className="mt-1 flex items-center justify-between gap-2 rounded-lg border border-caribbean bg-caribbean/10 px-3 py-2"
        >
          <div className="flex items-center gap-2 min-w-0">
            <CheckCircle2 size={16} className="text-caribbean shrink-0" />
            <span className="text-xs text-foreground truncate">
              {language === 'en' ? 'Uploaded' : 'Subido'}
            </span>
          </div>
          <button
            type="button"
            data-testid={`doc-${fieldId}-remove`}
            onClick={onRemove}
            className="w-6 h-6 rounded-full bg-destructive/15 hover:bg-destructive/25 text-destructive flex items-center justify-center"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <label
          data-testid={`doc-${fieldId}-input`}
          className={`mt-1 flex items-center gap-2 rounded-lg border-2 border-dashed border-border px-3 py-2.5 cursor-pointer hover:bg-muted/50 ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        >
          {uploading ? (
            <Loader2 size={16} className="text-caribbean animate-spin" />
          ) : (
            <FileText size={16} className="text-caribbean" />
          )}
          <span className="text-xs text-muted-foreground">
            {uploading
              ? (language === 'en' ? 'Uploading…' : 'Subiendo…')
              : (language === 'en' ? 'Tap to upload (JPG, PNG, PDF)' : 'Toca para subir (JPG, PNG, PDF)')}
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onSelect(f);
              e.target.value = '';
            }}
          />
        </label>
      )}
    </div>
  );
}

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const { t, language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [role, setRole] = useState('tourist');
  const [referralCode, setReferralCode] = useState('');
  const [referralPartner, setReferralPartner] = useState(null);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    companyName: '',
    description: '',
    categories: [],
    locations: [],
    id_document: null,
    tax_document: null,
  });
  const [uploadingDoc, setUploadingDoc] = useState(null); // 'id_document' | 'tax_document'

  const uploadDoc = async (field, file) => {
    if (!file) return;
    setUploadingDoc(field);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${API_URL}/api/upload/verification-docs`, {
        method: 'POST',
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Upload failed');
      setRegisterData((prev) => ({ ...prev, [field]: data.path }));
      toast.success(language === 'en' ? 'Document uploaded' : 'Documento subido');
    } catch (err) {
      console.error(err);
      toast.error(err?.message || (language === 'en' ? 'Upload failed' : 'Error al subir'));
    } finally {
      setUploadingDoc(null);
    }
  };

  const removeDoc = (field) => setRegisterData((prev) => ({ ...prev, [field]: null }));

  // Check for referral code in URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setActiveTab('register');
      setRole('tourist');
      // Validate the referral code
      validateReferralCode(ref.toUpperCase());
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/validate-referral/${code}`);
      const data = await response.json();
      if (data.valid) {
        setReferralPartner(data);
        toast.success(language === 'en' 
          ? `Welcome! You're registering with ${data.partner_name}'s code`
          : `¡Bienvenido! Te estás registrando con el código de ${data.partner_name}`);
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success(t('common.success'));
      const next = searchParams.get('next');
      navigate(next || '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (role === 'provider' && (!registerData.id_document || !registerData.tax_document)) {
      toast.error(language === 'en'
        ? 'Please upload both verification documents'
        : 'Sube ambos documentos de verificación');
      return;
    }
    setIsLoading(true);
    try {
      await register({
        email: registerData.email,
        password: registerData.password,
        name: registerData.name,
        phone: registerData.phone || null,
        role: role,
        company_name: role === 'provider' ? registerData.companyName : null,
        description: role === 'provider' ? registerData.description : null,
        categories: role === 'provider' ? registerData.categories : null,
        locations: role === 'provider' ? registerData.locations : null,
        referral_code: (role === 'tourist' && referralCode) ? referralCode : null,
        id_document: role === 'provider' ? registerData.id_document : null,
        tax_document: role === 'provider' ? registerData.tax_document : null,
      });
      toast.success(t('common.success'));
      const next = searchParams.get('next');
      navigate(next || '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCategory = (catId) => {
    setRegisterData(prev => ({
      ...prev,
      categories: prev.categories.includes(catId)
        ? prev.categories.filter(c => c !== catId)
        : [...prev.categories, catId]
    }));
  };

  const toggleLocation = (locId) => {
    setRegisterData(prev => ({
      ...prev,
      locations: prev.locations.includes(locId)
        ? prev.locations.filter(l => l !== locId)
        : [...prev.locations, locId]
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-muted/30 py-8 px-4">
      <div className="max-w-md mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft size={20} />
          <span>{t('common.back')}</span>
        </Link>

        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-heading text-center">
              {activeTab === 'login' ? t('auth.loginTitle') : t('auth.registerTitle')}
            </CardTitle>
            <CardDescription className="text-center">
              BidTrip
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">{t('auth.loginBtn')}</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">{t('auth.registerBtn')}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t('auth.email')}</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email"
                      type="email"
                      placeholder="email@example.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">{t('auth.password')}</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit"
                    className="w-full h-12 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold"
                    disabled={isLoading}
                  >
                    {isLoading ? t('common.loading') : t('auth.loginBtn')}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  {/* Referral Code Banner */}
                  {referralPartner && (
                    <div className="p-3 bg-gold/20 border border-gold rounded-xl flex items-center gap-3">
                      <Gift className="text-gold" size={20} />
                      <div>
                        <p className="text-sm font-medium">
                          {language === 'en' ? 'Referred by' : 'Referido por'} {referralPartner.partner_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {language === 'en' ? 'Code:' : 'Código:'} {referralCode}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Role Selection */}
                  <div className="space-y-2">
                    <Label>{t('auth.selectRole')}</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        data-testid="role-tourist"
                        onClick={() => setRole('tourist')}
                        className={`p-3 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                          role === 'tourist'
                            ? 'border-caribbean bg-caribbean/10'
                            : 'border-border hover:border-caribbean/50'
                        }`}
                      >
                        <User className={role === 'tourist' ? 'text-caribbean' : 'text-muted-foreground'} size={20} />
                        <span className={`text-sm ${role === 'tourist' ? 'text-caribbean font-medium' : 'text-muted-foreground'}`}>
                          {t('auth.tourist')}
                        </span>
                      </button>
                      <button
                        type="button"
                        data-testid="role-provider"
                        onClick={() => setRole('provider')}
                        className={`p-3 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                          role === 'provider'
                            ? 'border-caribbean bg-caribbean/10'
                            : 'border-border hover:border-caribbean/50'
                        }`}
                      >
                        <Briefcase className={role === 'provider' ? 'text-caribbean' : 'text-muted-foreground'} size={20} />
                        <span className={`text-sm ${role === 'provider' ? 'text-caribbean font-medium' : 'text-muted-foreground'}`}>
                          {t('auth.provider')}
                        </span>
                      </button>
                      <button
                        type="button"
                        data-testid="role-partner"
                        onClick={() => setRole('partner')}
                        className={`p-3 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                          role === 'partner'
                            ? 'border-gold bg-gold/10'
                            : 'border-border hover:border-gold/50'
                        }`}
                      >
                        <Users className={role === 'partner' ? 'text-gold' : 'text-muted-foreground'} size={20} />
                        <span className={`text-sm ${role === 'partner' ? 'text-gold font-medium' : 'text-muted-foreground'}`}>
                          Partner
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Referral Code Input for Tourists */}
                  {role === 'tourist' && !referralPartner && (
                    <div className="space-y-2">
                      <Label htmlFor="referral-code">
                        {language === 'en' ? 'Código Compa (optional)' : 'Código Compa (opcional)'}
                      </Label>
                      <Input
                        id="referral-code"
                        data-testid="referral-code"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        placeholder={language === 'en' ? 'Enter referral code' : 'Ingresa código de referido'}
                        className="h-12"
                        maxLength={6}
                      />
                    </div>
                  )}

                  {/* Basic Info */}
                  <div className="space-y-2">
                    <Label htmlFor="register-name">{t('auth.name')}</Label>
                    <Input
                      id="register-name"
                      data-testid="register-name"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">{t('auth.email')}</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email"
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">{t('auth.password')}</Label>
                    <Input
                      id="register-password"
                      data-testid="register-password"
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-phone">
                      {language === 'en' ? 'WhatsApp (for booking confirmations)' : 'WhatsApp (para confirmaciones)'}
                    </Label>
                    <Input
                      id="register-phone"
                      data-testid="register-phone"
                      type="tel"
                      placeholder="+52 ..."
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      className="h-12"
                    />
                  </div>

                  {/* Provider-specific fields */}
                  {role === 'provider' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="register-company">{t('auth.companyName')}</Label>
                        <Input
                          id="register-company"
                          data-testid="register-company"
                          value={registerData.companyName}
                          onChange={(e) => setRegisterData({ ...registerData, companyName: e.target.value })}
                          required
                          className="h-12"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="register-description">{t('auth.description')}</Label>
                        <Textarea
                          id="register-description"
                          data-testid="register-description"
                          value={registerData.description}
                          onChange={(e) => setRegisterData({ ...registerData, description: e.target.value })}
                          className="min-h-[80px]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{t('auth.selectCategories')}</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {CATEGORIES.map(cat => (
                            <div key={cat.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`cat-${cat.id}`}
                                checked={registerData.categories.includes(cat.id)}
                                onCheckedChange={() => toggleCategory(cat.id)}
                              />
                              <label htmlFor={`cat-${cat.id}`} className="text-sm cursor-pointer">
                                {language === 'es' ? cat.label_es : cat.label_en}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>{t('auth.selectLocations')}</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {LOCATIONS.map(loc => (
                            <div key={loc.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`loc-${loc.id}`}
                                checked={registerData.locations.includes(loc.id)}
                                onCheckedChange={() => toggleLocation(loc.id)}
                              />
                              <label htmlFor={`loc-${loc.id}`} className="text-sm cursor-pointer">
                                {loc.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Verification documents */}
                      <div className="space-y-3 rounded-xl bg-caribbean/5 border border-caribbean/30 p-3">
                        <div>
                          <p className="font-semibold text-sm flex items-center gap-1.5">
                            <FileText size={14} className="text-caribbean" />
                            {language === 'en' ? 'Verification documents' : 'Documentos de verificación'}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {language === 'en'
                              ? 'Required to register as a provider. JPG, PNG or PDF (max 10 MB each).'
                              : 'Obligatorios para registrarte como proveedor. JPG, PNG o PDF (máx 10 MB c/u).'}
                          </p>
                        </div>

                        {/* INE / Passport */}
                        <DocUploadField
                          fieldId="id_document"
                          labelEs="Identificación oficial (INE o Pasaporte) *"
                          labelEn="Official ID (INE or Passport) *"
                          value={registerData.id_document}
                          uploading={uploadingDoc === 'id_document'}
                          onSelect={(f) => uploadDoc('id_document', f)}
                          onRemove={() => removeDoc('id_document')}
                          language={language}
                        />

                        {/* RFC / Tax */}
                        <DocUploadField
                          fieldId="tax_document"
                          labelEs="Constancia de Situación Fiscal (RFC) *"
                          labelEn="Tax Document (RFC) *"
                          value={registerData.tax_document}
                          uploading={uploadingDoc === 'tax_document'}
                          onSelect={(f) => uploadDoc('tax_document', f)}
                          onRemove={() => removeDoc('tax_document')}
                          language={language}
                        />
                      </div>
                    </>
                  )}

                  <Button
                    type="submit"
                    data-testid="register-submit"
                    className="w-full h-12 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold"
                    disabled={isLoading}
                  >
                    {isLoading ? t('common.loading') : t('auth.registerBtn')}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
